import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiResponse } from '../models/api-response.model';
import {
  PagedListData,
  PagedListRequest,
} from '../models/paged-list.model';
import { ApiControllers } from '../constants/api-controllers.constants';
import { SizeCreateDto, SizeDto } from '../models/dtos/size.dto';
import {
  SalesDto,
  SalesInventoryItemDto,
  SalesInventorySearchRequest,
  SalesInvoiceNoDto,
  SalesListRequest,
  SalesPaymentsSaveDto,
  SalesSaveDto,
  SalesSaveResultDto,
} from '../models/dtos/sales.dto';
import {
  PurchaseAvailableBatchDto,
  PurchaseAvailableBatchRequest,
  PurchaseListPageDto,
  PurchaseListRequest,
  PurchaseSaveDto,
  PurchaseSaveResultDto,
} from '../models/dtos/purchase.dto';
import {
  UserListItemDto,
  UserListRequest,
} from '../models/dtos/user.dto';

/** Standard CRUD operations for a single API controller. */
export interface EntityCrudApi<TDto> {
  getList(
    request: PagedListRequest,
  ): Observable<ApiResponse<PagedListData<TDto>>>;
  getById(id: string): Observable<ApiResponse<TDto>>;
  save(dto: TDto): Observable<ApiResponse<unknown>>;
  delete(id: string): Observable<ApiResponse<unknown>>;
}

/**
 * Centralized CRUD API for standard backend controllers.
 * Use {@link crud} instead of per-module *Service classes.
 */
@Injectable({
  providedIn: 'root',
})
export class EntityApiService {
  private readonly api = inject(ApiService);

  crud<TDto>(controllerName: string): EntityCrudApi<TDto> {
    return {
      getList: (request) =>
        this.api.post<PagedListData<TDto>>(`${controllerName}/list`, request),
      getById: (id) => this.api.get<TDto>(`${controllerName}/${id}`),
      save: (dto) => this.api.post<unknown>(`${controllerName}/save`, dto),
      delete: (id) => this.api.delete<unknown>(`${controllerName}/${id}`),
    };
  }

  /** Customer/Supplier duplicate mobile check. */
  searchSizes(
    term?: string | null,
    maxRows = 50,
  ): Observable<ApiResponse<SizeDto[]>> {
    const params = new URLSearchParams();
    if (term?.trim()) {
      params.set('term', term.trim());
    }
    params.set('maxRows', String(maxRows));
    const qs = params.toString();
    return this.api.get<SizeDto[]>(
      `${ApiControllers.Size}/search${qs ? `?${qs}` : ''}`,
    );
  }

  createSize(size: string): Observable<ApiResponse<SizeDto>> {
    const payload: SizeCreateDto = { size: size.trim() };
    return this.api.post<SizeDto>(`${ApiControllers.Size}/create`, payload);
  }

  previewItemCode(options: {
    purityId: string;
    itemCodePrefix?: string | null;
    item?: string | null;
    excludeItemId?: string | null;
  }): Observable<ApiResponse<string | null>> {
    const params = new URLSearchParams({
      purityId: options.purityId,
    });
    const prefix = options.itemCodePrefix?.trim();
    if (prefix) {
      params.set('itemCodePrefix', prefix);
    }
    const item = options.item?.trim();
    if (item) {
      params.set('item', item);
    }
    if (options.excludeItemId) {
      params.set('excludeItemId', options.excludeItemId);
    }
    return this.api.get<string | null>(
      `${ApiControllers.Item}/preview-code?${params.toString()}`,
    );
  }

  /**
   * User list — custom body (searchText + userRole) instead of PagedListRequest.
   * Use {@link crud} for getById / save / delete.
   */
  getUserList(
    request: UserListRequest,
  ): Observable<ApiResponse<PagedListData<UserListItemDto>>> {
    return this.api.post<PagedListData<UserListItemDto>>(
      `${ApiControllers.User}/list`,
      request,
    );
  }

  checkMobileAvailable(
    controller:
      | typeof ApiControllers.Customer
      | typeof ApiControllers.Supplier
      | typeof ApiControllers.Salesman,
    mobile: string,
    excludeId?: string | null,
  ): Observable<ApiResponse<boolean>> {
    const params = new URLSearchParams({ mobile });
    if (excludeId) {
      const key =
        controller === ApiControllers.Customer
          ? 'excludeCustomerId'
          : controller === ApiControllers.Supplier
            ? 'excludeSupplierId'
            : 'excludeSalesmanId';
      params.set(key, excludeId);
    }
    return this.api.get<boolean>(`${controller}/check-mobile?${params.toString()}`);
  }

  checkCodeAvailable(
    controller:
      | typeof ApiControllers.Customer
      | typeof ApiControllers.Supplier
      | typeof ApiControllers.Salesman,
    code: string,
    excludeId?: string | null,
  ): Observable<ApiResponse<boolean>> {
    const params = new URLSearchParams({ code: code.trim().toUpperCase() });
    if (excludeId) {
      const key =
        controller === ApiControllers.Customer
          ? 'excludeCustomerId'
          : controller === ApiControllers.Supplier
            ? 'excludeSupplierId'
            : 'excludeSalesmanId';
      params.set(key, excludeId);
    }
    return this.api.get<boolean>(`${controller}/check-code?${params.toString()}`);
  }

  /** Sales list — extended filters (customer, date range). */
  getSalesList(
    request: SalesListRequest,
  ): Observable<ApiResponse<PagedListData<SalesDto>>> {
    return this.api.post<PagedListData<SalesDto>>(
      `${ApiControllers.Sales}/list`,
      request,
    );
  }

  getSalesNextInvoiceNo(): Observable<ApiResponse<SalesInvoiceNoDto>> {
    return this.api.get<SalesInvoiceNoDto>(
      `${ApiControllers.Sales}/next-invoice-no`,
    );
  }

  getSalesAvailableInventory(
    request: SalesInventorySearchRequest,
  ): Observable<ApiResponse<SalesInventoryItemDto[]>> {
    return this.api.post<SalesInventoryItemDto[]>(
      `${ApiControllers.Sales}/available-inventory`,
      request,
    );
  }

  saveSales(dto: SalesSaveDto): Observable<ApiResponse<SalesSaveResultDto>> {
    return this.api.post<SalesSaveResultDto>(
      `${ApiControllers.Sales}/save`,
      dto,
    );
  }

  saveSalesPayments(
    dto: SalesPaymentsSaveDto,
  ): Observable<ApiResponse<SalesSaveResultDto>> {
    return this.api.post<SalesSaveResultDto>(
      `${ApiControllers.Sales}/save-payments`,
      dto,
    );
  }

  getPurchaseList(
    request: PurchaseListRequest,
  ): Observable<ApiResponse<PurchaseListPageDto>> {
    return this.api.post<PurchaseListPageDto>(
      `${ApiControllers.Purchase}/list`,
      request,
    );
  }

  getPurchaseAvailableBatches(
    request: PurchaseAvailableBatchRequest,
  ): Observable<ApiResponse<PurchaseAvailableBatchDto[]>> {
    const params = new URLSearchParams();
    if (request.searchTerm?.trim()) {
      params.set('searchTerm', request.searchTerm.trim());
    }
    if (request.excludePurchaseId) {
      params.set('excludePurchaseId', request.excludePurchaseId);
    }
    if (request.supplierId) {
      params.set('supplierId', request.supplierId);
    }
    if (request.top != null) {
      params.set('top', String(request.top));
    }
    const qs = params.toString();
    return this.api.get<PurchaseAvailableBatchDto[]>(
      `${ApiControllers.Purchase}/available-batches${qs ? `?${qs}` : ''}`,
    );
  }

  savePurchase(
    dto: PurchaseSaveDto,
  ): Observable<ApiResponse<PurchaseSaveResultDto>> {
    return this.api.post<PurchaseSaveResultDto>(`${ApiControllers.Purchase}/save`, dto);
  }
}
